import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Phone, Globe, Mail, Calendar, ChevronLeft, Star, Send, X, Lock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_PHOTO = "https://images.pexels.com/photos/30892994/pexels-photo-30892994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const resolvePhoto = (url) => {
  if (!url) return DEFAULT_PHOTO;
  return url.startsWith("/") ? `${API}${url}` : url;
};

export default function ChefProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [chef, setChef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rvName, setRvName] = useState("");
  const [rvEmail, setRvEmail] = useState("");
  const [rvRating, setRvRating] = useState(0);
  const [rvText, setRvText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState("");
  const [reviewErr, setReviewErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/chefs/${id}`, { withCredentials: true })
      .then(r => setChef(r.data))
      .catch(() => setError("Chef not found"))
      .finally(() => setLoading(false));
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/api/chefs/${id}/reviews`);
      setReviews(data.reviews || []);
      setAverageRating(data.average_rating || 0);
    } catch {
      // silent
    }
  };

  const submitReview = async () => {
    setReviewErr("");
    if (!rvName.trim() || !rvEmail.trim() || rvRating < 1) {
      setReviewErr("Please add your name, email and a star rating.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/chefs/${id}/reviews`, {
        reviewer_name: rvName.trim(),
        reviewer_email: rvEmail.trim(),
        rating: rvRating,
        text: rvText.trim(),
        host_url: window.location.origin
      });
      setReviewMsg(data.message);
      setRvName(""); setRvEmail(""); setRvRating(0); setRvText("");
      setShowReviewForm(false);
    } catch (e) {
      setReviewErr(e.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-16">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !chef) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-16 text-center px-6">
      <div>
        <p className="font-serif text-3xl text-[#D4AF37] mb-4">Chef Not Found</p>
        <p className="text-[#A0A0A0] mb-6">This profile may no longer be available.</p>
        <Link to="/find-a-chef" className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 text-sm hover:bg-[#D4AF37] hover:text-black transition-all">
          Browse All Chefs
        </Link>
      </div>
    </div>
  );

  const availableDates = chef.availability_dates || [];
  const gallery = chef.gallery_urls || [];

  return (
    <div className="min-h-screen bg-[#050505] pt-16">
      {/* Hero */}
      <div className="relative h-80 sm:h-96 overflow-hidden">
        <img
          src={resolvePhoto(chef.photo_url)}
          alt={chef.chef_name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-7xl mx-auto">
          <Link to="/find-a-chef" className="flex items-center gap-1 text-[#A0A0A0] hover:text-white text-sm mb-4 transition-colors w-fit">
            <ChevronLeft size={14} /> Back to Directory
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={14} className="text-[#D4AF37]" />
                <span className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">
                  {chef.regions?.join(", ") || "United Kingdom"}
                  {chef.specific_region && ` · ${chef.specific_region}`}
                </span>
              </div>
              <h1 data-testid="chef-name" className="font-serif text-4xl sm:text-5xl text-white font-light mb-4">
                {chef.chef_name}
              </h1>
              {reviews.length > 0 && (
                <div data-testid="chef-rating-summary" className="flex items-center gap-3 mb-4">
                  <StarRow value={Math.round(averageRating)} size={16} />
                  <span className="text-[#D4AF37] font-serif text-lg">{averageRating.toFixed(1)}</span>
                  <span className="text-[#A0A0A0] text-sm">({reviews.length} {reviews.length === 1 ? "review" : "reviews"})</span>
                </div>
              )}
              {chef.price_range_min && (
                <div className="inline-flex items-center border border-[#D4AF37]/30 px-4 py-2 mb-4">
                  <span className="text-[#D4AF37] font-serif text-lg">£{chef.price_range_min}</span>
                  {chef.price_range_max && <span className="text-[#D4AF37] font-serif text-lg"> – £{chef.price_range_max}</span>}
                  <span className="text-[#A0A0A0] text-sm ml-2">per event</span>
                </div>
              )}
            </div>

            {chef.bio && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">About</p>
                <p data-testid="chef-bio" className="text-[#A0A0A0] leading-relaxed text-base">{chef.bio}</p>
              </div>
            )}

            {gallery.length > 0 && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">Gallery</p>
                <div data-testid="chef-gallery" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {gallery.map(url => (
                    <button
                      key={url}
                      data-testid="gallery-item"
                      onClick={() => setLightboxUrl(resolvePhoto(url))}
                      className="aspect-square overflow-hidden bg-[#0A0A0A] border border-[#D4AF37]/15 hover:border-[#D4AF37]/60 transition-colors group"
                    >
                      <img src={resolvePhoto(url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(chef.cuisine_specialties || []).length > 0 && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">Cuisine Specialties</p>
                <div className="flex flex-wrap gap-2">
                  {chef.cuisine_specialties.map(c => (
                    <span key={c} className="border border-[#D4AF37]/30 text-white px-4 py-2 text-sm">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {(chef.service_types || []).length > 0 && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">Services Offered</p>
                <div className="flex flex-wrap gap-2">
                  {chef.service_types.map(s => (
                    <span key={s} className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#A0A0A0] px-4 py-2 text-sm">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {availableDates.length > 0 && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
                  <Calendar size={12} className="inline mr-2" />
                  Available Dates
                </p>
                <div className="flex flex-wrap gap-2">
                  {availableDates.slice(0, 20).map(d => (
                    <span key={d} className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1.5 text-sm font-mono">
                      {d}
                    </span>
                  ))}
                  {availableDates.length > 20 && (
                    <span className="text-[#A0A0A0] text-sm px-2 py-1.5">+{availableDates.length - 20} more</span>
                  )}
                </div>
              </div>
            )}

            {/* REVIEWS */}
            <div className="border-t border-[#D4AF37]/15 pt-8">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Client Reviews</p>
                <button
                  data-testid="leave-review-btn"
                  onClick={() => { setShowReviewForm(s => !s); setReviewMsg(""); setReviewErr(""); }}
                  className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-5 py-2 text-xs tracking-widest uppercase font-medium transition-colors"
                >
                  {showReviewForm ? "Cancel" : "Leave a Review"}
                </button>
              </div>

              {reviewMsg && (
                <div data-testid="review-success-msg"
                  className="mb-6 bg-emerald-900/20 border border-emerald-800/30 text-emerald-400 px-4 py-3 text-sm">
                  {reviewMsg}
                </div>
              )}

              {showReviewForm && (
                <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-6 mb-8 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      data-testid="review-name-input"
                      type="text" value={rvName} onChange={e => setRvName(e.target.value)}
                      placeholder="Your name"
                      className="bg-transparent border border-white/15 focus:border-[#D4AF37] px-3 py-2.5 text-white placeholder-white/25 text-sm outline-none transition-colors"
                    />
                    <input
                      data-testid="review-email-input"
                      type="email" value={rvEmail} onChange={e => setRvEmail(e.target.value)}
                      placeholder="Your email (for verification)"
                      className="bg-transparent border border-white/15 focus:border-[#D4AF37] px-3 py-2.5 text-white placeholder-white/25 text-sm outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-[#D4AF37] mb-2">Your Rating</p>
                    <StarPicker value={rvRating} onChange={setRvRating} />
                  </div>
                  <textarea
                    data-testid="review-text-input"
                    value={rvText} onChange={e => setRvText(e.target.value)}
                    rows={4} placeholder="Share your experience (optional)"
                    className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] px-3 py-2.5 text-white placeholder-white/25 text-sm outline-none transition-colors resize-none"
                  />
                  {reviewErr && <p className="text-red-400 text-sm">{reviewErr}</p>}
                  <p className="text-xs text-[#555]">We'll email you a verification link — your review goes live once you confirm.</p>
                  <button
                    data-testid="submit-review-btn"
                    onClick={submitReview}
                    disabled={submitting}
                    className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-6 py-3 text-sm tracking-widest uppercase font-medium transition-colors disabled:opacity-40 inline-flex items-center gap-2"
                  >
                    <Send size={14} /> {submitting ? "Submitting..." : "Submit Review"}
                  </button>
                </div>
              )}

              {reviews.length === 0 ? (
                <p className="text-[#A0A0A0] text-sm italic">No reviews yet. Be the first to share your experience.</p>
              ) : (
                <div className="space-y-5" data-testid="reviews-list">
                  {reviews.map(r => (
                    <div key={r.id} data-testid={`review-${r.id}`} className="bg-[#0A0A0A] border-l-2 border-[#D4AF37]/40 px-5 py-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-white font-medium">{r.reviewer_name}</span>
                          <StarRow value={r.rating} size={13} />
                        </div>
                        <span className="text-[#555] text-xs">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : ""}
                        </span>
                      </div>
                      {r.text && <p className="text-[#A0A0A0] text-sm leading-relaxed">{r.text}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {chef.contact_locked ? (
              <div data-testid="contact-locked-card" className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Lock size={14} className="text-[#D4AF37]" />
                  <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Contact Details Locked</p>
                </div>
                <p className="text-[#A0A0A0] text-sm leading-relaxed mb-5">
                  {user === false || user === null
                    ? "Sign in with a verified email account to view this chef's full name, email, phone and website."
                    : user.role !== "client"
                      ? "Contact details are available to verified client accounts only."
                      : "Please verify your email address to unlock chef contact details. Check your inbox for the verification link we sent when you registered."}
                </p>
                {(user === false || user === null) ? (
                  <div className="flex flex-col gap-3">
                    <Link
                      to="/register"
                      data-testid="contact-locked-register-btn"
                      className="block w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-3 text-sm font-medium tracking-widest uppercase text-center transition-colors"
                    >
                      Create Account
                    </Link>
                    <Link
                      to="/login"
                      data-testid="contact-locked-login-btn"
                      className="block w-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 py-3 text-sm tracking-widest uppercase text-center transition-colors"
                    >
                      Log In
                    </Link>
                  </div>
                ) : user.role === "client" && !user.email_verified ? (
                  <ResendVerificationButton />
                ) : null}
              </div>
            ) : (
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-5">Get in Touch</p>
                <div className="space-y-4">
                  {chef.chef_email && (
                    <a href={`mailto:${chef.chef_email}`} data-testid="chef-email-link"
                      className="flex items-center gap-3 text-[#A0A0A0] hover:text-white text-sm transition-colors group">
                      <Mail size={16} className="text-[#D4AF37]" />
                      <span className="truncate">{chef.chef_email}</span>
                    </a>
                  )}
                  {chef.phone && (
                    <a href={`tel:${chef.phone}`} data-testid="chef-phone"
                      className="flex items-center gap-3 text-[#A0A0A0] hover:text-white text-sm transition-colors">
                      <Phone size={16} className="text-[#D4AF37]" />
                      {chef.phone}
                    </a>
                  )}
                  {chef.website && (
                    <a href={chef.website} target="_blank" rel="noreferrer"
                      className="flex items-center gap-3 text-[#A0A0A0] hover:text-white text-sm transition-colors">
                      <Globe size={16} className="text-[#D4AF37]" />
                      Visit Website
                    </a>
                  )}
                </div>
                {chef.chef_email && (
                  <a
                    href={`mailto:${chef.chef_email}?subject=Private Chef Enquiry`}
                    data-testid="chef-enquiry-btn"
                    className="mt-6 block w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-3 text-sm font-medium tracking-widest uppercase text-center transition-colors duration-200"
                  >
                    Send Enquiry
                  </a>
                )}
              </div>
            )}

            {(chef.regions || []).length > 0 && (
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">Regions Covered</p>
                <div className="space-y-2">
                  {chef.regions.map(r => (
                    <div key={r} className="flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                      <span className="text-[#A0A0A0] text-sm">{r}</span>
                    </div>
                  ))}
                  {chef.specific_region && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5">
                      <div className="w-1 h-1 rounded-full bg-[#D4AF37]/50" />
                      <span className="text-[#A0A0A0] text-sm italic">{chef.specific_region}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div data-testid="lightbox"
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6 cursor-pointer"
          onClick={() => setLightboxUrl(null)}
        >
          <button onClick={() => setLightboxUrl(null)} className="absolute top-6 right-6 text-white hover:text-[#D4AF37]">
            <X size={28} />
          </button>
          <img src={lightboxUrl} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function ResendVerificationButton() {
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const handleResend = async () => {
    setSending(true); setMsg("");
    try {
      await axios.post(`${API}/api/auth/resend-verification`,
        { host_url: window.location.origin }, { withCredentials: true });
      setMsg("Verification email sent — check your inbox.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to send verification email");
    } finally {
      setSending(false);
    }
  };
  return (
    <div>
      <button
        type="button"
        onClick={handleResend}
        disabled={sending}
        data-testid="resend-verification-btn"
        className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-3 text-sm font-medium tracking-widest uppercase transition-colors disabled:opacity-40"
      >
        {sending ? "Sending..." : "Resend Verification Email"}
      </button>
      {msg && <p className="text-xs text-[#A0A0A0] mt-3">{msg}</p>}
    </div>
  );
}

function StarRow({ value, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n} size={size}
          className={n <= value ? "text-[#D4AF37] fill-[#D4AF37]" : "text-white/15"}
        />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="review-rating-picker">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n} type="button"
          data-testid={`star-${n}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="p-1 transition-transform hover:scale-110"
        >
          <Star
            size={26}
            className={n <= (hover || value) ? "text-[#D4AF37] fill-[#D4AF37]" : "text-white/20"}
          />
        </button>
      ))}
      {value > 0 && <span className="text-[#A0A0A0] text-sm ml-2">{value} / 5</span>}
    </div>
  );
}
